import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save, UserRound } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { AppointmentUpdate, Service } from '../../../../../packages/contracts/src/index.js';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Input, Label, Select, Textarea } from '../../components/ui/field';
import { CustomerSearchBox } from '../../components/ui/search';
import { ErrorState, LoadingState } from '../../components/ui/state';
import { DateShortcutButtons, localDateTimeInput } from '../../components/ui/time-range';
import { api } from '../../shared/api';
import { labelForServiceCategory } from '../../shared/i18n';
import { useAuth } from '../auth/AuthProvider';

const serviceCategories: Service['category'][] = ['grooming', 'spa', 'transportation'];

function toLocalInputValue(value: string) {
  return localDateTimeInput(new Date(value));
}

function toIsoDateTime(value: string) {
  return new Date(value).toISOString();
}

export function AppointmentEditPage() {
  const { user } = useAuth();
  const isAdministrator = user?.groups.includes('Administrator') ?? false;
  const { appointmentId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const appointmentQuery = useQuery({ queryKey: ['appointment', appointmentId], queryFn: () => api.appointments.get(appointmentId), enabled: Boolean(appointmentId) });
  const [customerId, setCustomerId] = useState('');
  const [serviceCategory, setServiceCategory] = useState<Service['category']>('grooming');
  const [selectedDogIds, setSelectedDogIds] = useState<string[]>([]);
  const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
  const [scheduledAt, setScheduledAt] = useState(() => localDateTimeInput(new Date()));
  const [notes, setNotes] = useState('');

  const dogsQuery = useQuery({ queryKey: ['customer-dogs', customerId, 'appointments'], queryFn: () => api.customers.allDogs(customerId), enabled: Boolean(customerId) });
  const servicesQuery = useQuery({ queryKey: ['services'], queryFn: () => api.services.list({ limit: 100 }) });
  const categoryServices = (servicesQuery.data?.items ?? []).filter((service) => service.category === serviceCategory && service.active !== false);

  useEffect(() => {
    const appointment = appointmentQuery.data;
    if (!appointment) return;
    setCustomerId(appointment.customerId);
    setServiceCategory(appointment.serviceCategory);
    setSelectedDogIds(appointment.dogIds);
    setSelectedServiceIds(appointment.serviceIds);
    setScheduledAt(toLocalInputValue(appointment.scheduledAt));
    setNotes(appointment.notes ?? '');
  }, [appointmentQuery.data]);

  const updateAppointment = useMutation({
    mutationFn: () => {
      const input: AppointmentUpdate = {
        dogIds: selectedDogIds,
        serviceCategory,
        serviceIds: selectedServiceIds,
        scheduledAt: toIsoDateTime(scheduledAt),
        notes: notes || undefined,
      };
      return api.appointments.update(appointmentId, input);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['appointments'] });
      await queryClient.invalidateQueries({ queryKey: ['appointment', appointmentId] });
      navigate('/admin/grooming');
    },
  });

  const toggleDog = (dogId: string) => setSelectedDogIds((current) => current.includes(dogId) ? current.filter((id) => id !== dogId) : [...current, dogId]);
  const toggleService = (serviceId: string) => setSelectedServiceIds((current) => current.includes(serviceId) ? current.filter((id) => id !== serviceId) : [...current, serviceId]);

  if (appointmentQuery.isLoading) return <LoadingState label="Cargando cita" />;
  if (appointmentQuery.isError) return <ErrorState message={appointmentQuery.error.message} />;

  return (
    <div className="max-w-2xl space-y-5">
      <div>
        <h1 className="text-2xl font-semibold text-slate-950">Editar cita de grooming</h1>
        <p className="mt-1 text-sm text-slate-500">Actualiza cliente, perros, horario y servicios asociados.</p>
      </div>
      <Card>
        <CardHeader><h2 className="text-sm font-semibold">Datos de la cita</h2></CardHeader>
        <CardContent>
          <form className="grid gap-3" onSubmit={(event) => { event.preventDefault(); updateAppointment.mutate(); }}>
            {isAdministrator && appointmentQuery.data?.completedByName ? (
              <div className="flex items-center gap-3 rounded-md border border-[#d6ded0] bg-[#fbfcfa] p-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-[#eef3ea] text-[#596a52]"><UserRound className="h-4 w-4" aria-hidden="true" /></span>
                <div><p className="text-xs font-semibold uppercase text-[#687461]">Grooming realizado por</p><p className="mt-0.5 text-sm font-medium text-[#223126]">{appointmentQuery.data.completedByName}</p></div>
              </div>
            ) : null}
            <div className="grid gap-1.5">
              <Label htmlFor="appointmentCustomer">Cliente</Label>
              <CustomerSearchBox id="appointmentCustomer" value={customerId} placeholder="Selecciona un cliente" onChange={(id) => { setCustomerId(id); setSelectedDogIds([]); }} />
            </div>
            <div className="grid gap-1.5">
              <Label>Perros</Label>
              <div className="grid gap-2 rounded-md border border-[#d6ded0] bg-white p-3">
                {!customerId ? <p className="text-sm text-slate-500">Selecciona un cliente primero.</p> : null}
                {(dogsQuery.data?.items ?? []).map((dog) => (
                  <label key={dog.id} className="flex items-center gap-2 text-sm text-[#334033]">
                    <input type="checkbox" checked={selectedDogIds.includes(dog.id)} onChange={() => toggleDog(dog.id)} />
                    {dog.name}
                  </label>
                ))}
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="grid gap-1.5">
                <Label htmlFor="serviceCategory">Tipo</Label>
                <Select id="serviceCategory" value={serviceCategory} onChange={(event) => { setServiceCategory(event.target.value as Service['category']); setSelectedServiceIds([]); }}>
                  {serviceCategories.map((category) => <option key={category} value={category}>{labelForServiceCategory(category)}</option>)}
                </Select>
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="scheduledAt">Fecha y hora</Label>
                <Input id="scheduledAt" type="datetime-local" value={scheduledAt} onChange={(event) => setScheduledAt(event.target.value)} />
                <DateShortcutButtons unit="hours" onToday={() => setScheduledAt(localDateTimeInput(new Date()))} />
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label>Servicios</Label>
              <div className="grid gap-2 rounded-md border border-[#d6ded0] bg-white p-3">
                {categoryServices.map((service) => (
                  <label key={service.id} className="flex items-center gap-2 text-sm text-[#334033]">
                    <input type="checkbox" checked={selectedServiceIds.includes(service.id)} onChange={() => toggleService(service.id)} />
                    {service.name}
                  </label>
                ))}
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="appointmentNotes">Notas</Label>
              <Textarea id="appointmentNotes" value={notes} onChange={(event) => setNotes(event.target.value)} />
            </div>
            {updateAppointment.isError ? <p className="text-sm text-rose-600">{updateAppointment.error.message}</p> : null}
            <Button type="submit" disabled={!customerId || selectedDogIds.length === 0 || selectedServiceIds.length === 0 || updateAppointment.isPending}>
              <Save className="h-4 w-4" aria-hidden="true" />
              Guardar cita
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
